// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import showdown from 'showdown';

import type { AIMessageDraft } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { applyPersonalizationPlaceholders } from 'bundles/enterprise-admin-messaging-v2/utils/personalizationTags';

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const escapeHtml = (value: string): string => value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char);

// Escape characters that would break out of a `href="..."`. Plain HTML escape
// is enough because the value is always quoted with double quotes below.
const escapeAttr = (value: string): string => escapeHtml(value);

// The message body is authored as markdown (see MessageBodyEditor). Convert it
// to HTML for the email. `simpleLineBreaks` makes single newlines render as
// <br>, matching what the admin sees in the editor.
const markdownConverter = new showdown.Converter({
  simpleLineBreaks: true,
  simplifiedAutoLink: false,
  tables: false,
});

const renderBody = (markdown: string): string => markdownConverter.makeHtml(markdown);

// Canonical Coursera wordmark used in transactional emails (mailspin asset),
// served through the image proxy. Row 15: replaces the prior Georgia-serif text
// "coursera", which wasn't the real logo.
const COURSERA_WORDMARK_URL =
  'https://d3njjcbhbojbot.cloudfront.net/api/utilities/v1/imageproxy/https://coursera-misc.s3.amazonaws.com/mailspin/prod/11_03_2020_18_12_04_coursera-wordmark-blue.png';

type CompileOptions = {
  /**
   * When true, personalization tokens (e.g. `[Learner name]`) are converted to
   * the backend `<ptag>…</ptag>` placeholders for sending. When false (preview),
   * the tokens are left as readable text so the admin sees them in place.
   */
  forSend?: boolean;
};

/**
 * Compiles the draft into the email HTML.
 *
 * Stand-in for the backend-compiled email HTML: once the messaging service
 * returns ready-to-render HTML, replace the body of this function; the call
 * sites and iframe rendering stay the same.
 *
 * The HTML is inline-styled so it renders identically inside a sandboxed
 * iframe with no external stylesheet.
 */
export const mockCompileEmailHtml = (draft: AIMessageDraft, { forSend = false }: CompileOptions = {}): string => {
  const subject = escapeHtml(draft.subject);
  const ctaText = escapeHtml(draft.ctaText);
  // Reject anything that isn't http/https so a `javascript:` URL can't appear
  // verbatim in the rendered href even if the iframe sandbox is ever relaxed.
  const isSafeUrl = /^https?:\/\//i.test(draft.ctaUrl);
  const ctaUrl = isSafeUrl ? escapeAttr(draft.ctaUrl) : '#';
  const renderedBody = renderBody(draft.message);
  // For sending, swap the readable tokens for the backend placeholders.
  const body = forSend ? applyPersonalizationPlaceholders(renderedBody) : renderedBody;

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${subject}</title>
</head>
<body style="margin:0;padding:24px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1f1f1f;">
  <div style="text-align:center;">
    <img src="${COURSERA_WORDMARK_URL}" alt="Coursera" width="140" style="display:inline-block;height:auto;border:0;" />
  </div>
  <div style="border-top:1px solid #dae1ed;margin:24px 0;"></div>
  <div style="font-size:14px;line-height:22px;color:#1f1f1f;">
    ${body}
  </div>
  <div style="margin-top:8px;">
    <a href="${ctaUrl}" style="display:inline-block;background:#0056d2;color:#ffffff;text-decoration:none;font-weight:600;font-size:14px;line-height:20px;letter-spacing:0.14px;padding:8px 16px;border-radius:8px;">${ctaText}</a>
  </div>
  <div style="border-top:1px solid #dae1ed;margin:24px 0 0;"></div>
  <p style="margin-top:16px;font-size:11px;line-height:16px;color:#6a6a6a;">© Your Organization. You're receiving this email because you're enrolled in a learning program. <a href="#" style="color:#6a6a6a;text-decoration:underline;">Unsubscribe</a>.</p>
</body>
</html>`;
};
